import { Controller } from "@hotwired/stimulus";

/**
 * The membership card upload sheet: the same base64 hand-off as the image upload, plus the
 * empty/attached swap the sheet is built around.
 *
 * The file input stays hidden and is opened by a real button rather than by a label dressed as
 * one, so the control is focusable, announces itself and takes a keyboard. One input, because the
 * operating system's own sheet already offers the camera and the library; see the template.
 *
 * Every target but `blob`, `input` and `preview` is optional, so the three-element form this
 * started as still drives it.
 */
export default class extends Controller<HTMLElement> {
  static override targets = [
    "attached",
    "blob",
    "empty",
    "input",
    "memberId",
    "name",
    "preview",
    "submit",
  ];

  /** The "Photo ready {0}" sentence, so the filename the browser learns reads like the one the server renders. */
  static override values = { ready: String };

  declare readonly attachedTarget: HTMLElement;
  declare readonly blobTarget: HTMLInputElement;
  declare readonly emptyTarget: HTMLElement;
  declare readonly hasAttachedTarget: boolean;
  declare readonly hasEmptyTarget: boolean;
  declare readonly hasMemberIdTarget: boolean;
  declare readonly hasNameTarget: boolean;
  declare readonly hasSubmitTarget: boolean;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly inputTargets: HTMLInputElement[];
  declare readonly memberIdTarget: HTMLInputElement;
  declare readonly nameTarget: HTMLElement;
  declare readonly previewTarget: HTMLImageElement;
  declare readonly readyValue: string;
  declare readonly submitTarget: HTMLButtonElement;

  override connect(): void {
    this.render();
  }

  openPicker(): void {
    this.inputTarget.click();
  }

  onChange(): void {
    const file = this.inputTargets
      .map((it) => it.files?.[0])
      .find((it) => it !== undefined);
    if (file === undefined) {
      return;
    }

    if (this.hasNameTarget) {
      this.nameTarget.textContent = this.readyValue
        ? this.readyValue.replace("{0}", file.name)
        : file.name;
    }

    const reader = new FileReader();
    reader.onloadend = () => this.save(reader.result);
    reader.readAsDataURL(file);
  }

  save(result: ArrayBuffer | string | null): void {
    if (typeof result !== "string") {
      return;
    }

    this.blobTarget.value = result.split(",")[1] ?? "";
    this.inputTarget.value = "";
    this.previewTarget.src = result;
    this.previewTarget.classList.remove("membership-document-placeholder");

    this.render();
  }

  /** The member number gates the action as much as the photo does. */
  onMemberIdInput(): void {
    this.render();
  }

  /** Back to the empty state: the photo, the bytes that were going to be posted, and the name. */
  remove(): void {
    this.blobTarget.value = "";
    this.previewTarget.removeAttribute("src");
    this.inputTargets.forEach((it) => (it.value = ""));

    if (this.hasNameTarget) {
      this.nameTarget.textContent = "";
    }

    this.render();
  }

  /** The upload is a plain form post, so this is the only moment the sheet can say it is working. */
  onSubmit(): void {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
      this.submitTarget.dataset["loading"] = "true";
    }
  }

  private render(): void {
    // A card already on file arrives as a `src` with no blob, which is just as attached.
    const attached =
      this.blobTarget.value !== "" ||
      (this.previewTarget.getAttribute("src") ?? "") !== "";

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = attached;
    }
    if (this.hasAttachedTarget) {
      this.attachedTarget.hidden = !attached;
    }
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !attached || !this.hasMemberId();
    }
  }

  private hasMemberId(): boolean {
    return !this.hasMemberIdTarget || this.memberIdTarget.value.trim() !== "";
  }
}
